import React, { useState, useEffect, useRef } from "react";
import "./sidebar.scss";
import { Sidebar, Menu, MenuItem } from "react-pro-sidebar";

import { AiOutlinePlus, AiOutlineFile } from "react-icons/ai";
import { MdFolderShared } from "react-icons/md";
import { BiTransferAlt } from "react-icons/bi";
import { AiOutlineApi } from "react-icons/ai";
import { HiOutlineDocument } from "react-icons/hi";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { balanceAC, otherDataAC } from "../../store/action-creators";
import {
  getBalance,
  uploadEncryptedFile,
  uploadFile,
  uploadFolder,
} from "../../utils/services/lighthouse";
import { bytesToString } from "../../utils/services/other";
import History from "../../utils/services/GlobalNavigation/navigationHistory";
import { CgProfile } from "react-icons/cg";
import { RiAdminLine } from "react-icons/ri";

import { ToggleButton } from "../../components";
import { Popover } from "react-tiny-popover";
import { BsFolder2Open, BsInfoCircle } from "react-icons/bs";
import { LinearProgress, linearProgressClasses, styled } from "@mui/material";
import { Tooltip } from "react-tooltip";
import { isAdminLogin } from "../../utils/services/auth";
import { MdArrowOutward } from "react-icons/md";


import { notify } from "../../utils/services/notification";


interface SidebarProps {
  setUploadProgress: any;
  setUploadData: any;
}

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.mode === "light" ? "#1a90ff" : "#308fe8",
  },
}));

const SidebarContainer: React.FC<SidebarProps> = ({
  setUploadProgress,
  setUploadData,
}) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const [currentPage, setCurrentPage] = useState<string>("dashboard");
  const [isEncrypted, setIsEncrypted] = useState<boolean>(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [userBalance, setUserBalance] = useState<any>(null);

  const _location = useLocation();
  const dispatch = useDispatch();
  const store = useSelector((store: any) => store);
  const _otherData = bindActionCreators(otherDataAC, dispatch);
  const _balanceAC = bindActionCreators(balanceAC, dispatch);

  useEffect(() => {
    let componentMounted = true;
    if (componentMounted) {
      (async () => {
        await getBalance();
      })();
    }
    return () => {
      componentMounted = false;
    };
  }, []);

  useEffect(() => {
    let componentMounted = true;
    if (componentMounted) {
      setUserBalance(store["balance"]);
      if (fileInput.current) fileInput.current.value = "";
      if (folderInput.current) folderInput.current.value = "";
    }

    return () => {
      componentMounted = false;
    };
  }, [store["balance"]]);

  useEffect(() => {
    let componentMounted = true;

    if (componentMounted) {
      setCurrentPage(_location.pathname);
      store?.otherData?.isMobile &&
        _otherData.setOtherData({ sidebarClosed: true });
    }
    return () => {
      componentMounted = false;
    };
  }, [_location]);

  const getEventFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadData(e.target.files[0].name);
      setUploadProgress(1);
      isEncrypted
        ? uploadEncryptedFile(e, setUploadProgress)
        : uploadFile(e, setUploadProgress);
    }
  };

  const getEventFolder = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadData(e.target.files[0].webkitRelativePath.split("/")[0]);
      setUploadProgress(1);
      uploadFolder(e, setUploadProgress);
    }
  };

  return (
    <Sidebar
      className="SidebarSlider"
      collapsed={store?.otherData?.sidebarClosed}
      collapsedWidth="0px"
      width="270px"
    >
      <section className="SidebarSlider__header">
        {
          <div style={{ padding: "0rem 1.5rem" }}>
            <ToggleButton
              setTrue={setIsEncrypted}
              isTrue={isEncrypted}
              trueValue={"Encryption <br/> ON"}
              falseValue={"Encryption <br/> OFF"}
              type={"encrypted"}
            />
          </div>
        }

        <Menu>
          {
            (store?.otherData?.isOlderThanThirtyDays && store?.balance?.dataLimit === 1073741824) ? <MenuItem
              icon={<AiOutlinePlus />}
              active={false}
              onClick={() => {
                notify('Trial Period Over | Purchase Plan To Continue Using Lighthouse', 'error')

              }}
            >
              Upload New
            </MenuItem> : <Popover
              isOpen={isPopoverOpen}
              positions={["bottom"]}
              containerClassName="uploadPopoverContainer"
              onClickOutside={() => setIsPopoverOpen(false)}
              content={
                <div className="uploadPopover">
                  <div
                    className="uploadPopover__btn"
                    onClick={() => {
                      fileInput?.current?.click();
                    }}
                  >
                    <span>
                      <AiOutlineFile />
                    </span>
                    Upload File
                  </div>

                  {!isEncrypted && (
                    <div
                      className="uploadPopover__btn"
                      onClick={() => {
                        folderInput?.current?.click();
                      }}
                    >
                      <span>
                        <BsFolder2Open />
                      </span>
                      Upload Folder
                    </div>
                  )}
                </div>
              }
            >
              <MenuItem
                icon={<AiOutlinePlus />}
                active={false}
                onClick={() => {
                  isPopoverOpen
                    ? setIsPopoverOpen(false)
                    : setIsPopoverOpen(true);
                }}
              >
                Upload New
              </MenuItem>
            </Popover>
          }

        </Menu>
      </section>
      <hr />

      <section className="SidebarSlider__menuContainer">
        <Menu
          menuItemStyles={{
            button: {
              [`&.ps-active`]: {
                backgroundColor: "#494949",
                color: "#ffff",
              },
            },
          }}
        >
          <MenuItem
            icon={<MdFolderShared />}
            component={<Link to="/dashboard" />}
            active={currentPage === "/dashboard" ? true : false}
          >
            Dashboard
          </MenuItem>
          <MenuItem
            icon={<BiTransferAlt />}
            component={<Link to="/dashboard/migration" />}
            active={currentPage === "/dashboard/migration" ? true : false}
          >
            Migration
          </MenuItem>

          <MenuItem
            icon={<AiOutlineApi />}
            component={<Link to="/dashboard/apikey" />}
            active={currentPage === "/dashboard/apikey" ? true : false}
          >
            API Key
          </MenuItem>

          <MenuItem
            icon={<CgProfile />}
            component={<Link to="/dashboard/profile" />}
            active={currentPage === "/dashboard/profile" ? true : false}
          >
            Profile
          </MenuItem>
          {
            isAdminLogin() && <MenuItem
              icon={<RiAdminLine />}
              component={<Link to="/dashboard/admin" />}
              active={currentPage === "/dashboard/admin" ? true : false}
            >
              Admin
            </MenuItem>
          }


        </Menu>
      </section>

      <section className="SidebarSlider__menuContainer" style={{display:'flex',alignItems:'flex-end'}}>
        <Menu
          menuItemStyles={{
            button: {
              [`&.ps-active`]: {
                backgroundColor: "#494949",
                color: "#ffff",
              },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              window.open(
                "https://docs.lighthouse.storage/lighthouse-1/",
                "_blank"
              );
            }}
          >
            Documentation{" "} <MdArrowOutward/>
          </MenuItem>
        </Menu>
      </section>
      <hr />



      <section className="SidebarSlider__footer">
        <div className="storageBox">
          <p className="storageBox__main">
            {bytesToString(userBalance?.["dataUsed"])} &nbsp;<small>Used</small>
          </p>
          <p className="storageBox__sub">
            {(
              (userBalance?.["dataUsed"] / userBalance?.["dataLimit"]) *
              100
            ).toFixed(2)}
            % used -{" "}
            {bytesToString(
              userBalance?.["dataLimit"] - userBalance?.["dataUsed"]
            )}{" "}
            free
          </p>

          <div className="storageBox__progressBox">
            <BorderLinearProgress
              variant="determinate"
              value={parseFloat(
                (
                  (userBalance?.["dataUsed"] / userBalance?.["dataLimit"]) *
                  100
                ).toFixed(2)
              )}
            />
          </div>
          {
            store?.["balance"]?.["allTransactions"]?.length > 0 ? <></> : <p>
              Free 14 day trial &nbsp;
              <BsInfoCircle
                data-tooltip-id="Sidebar-tooltip"
                data-tooltip-content={"Update plan to keep your data"}
              />
            </p>
          }

        </div>
        <div
          className="buyBtn"
          onClick={() => {
            History.push("/dashboard/topup");
          }}
        >
          Get More Storage
        </div>
      </section>
      <input
        type="file"
        id="file"
        ref={fileInput}
        onChange={(e) => getEventFile(e)}
        hidden
      />
      <input
        ref={folderInput}
        onChange={(e) => getEventFolder(e)}
        type="file"
        id="folder"
        hidden
        // @ts-ignore
        webkitdirectory="true"
      />

      <Tooltip id="Sidebar-tooltip" />
    </Sidebar>
  );
};

export default SidebarContainer;
